import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import type {
  CHANGE_SET_EXTRACTOR_TYPE,
  NameConverter,
  OnRevert,
  PivotClassifier,
  PivotValueCalculator,
  QueryToFormConverter,
  ValueConverter,
} from '@/_containers';
import { historyItemStringConverter, newValueCalculator } from '@/_containers';
import type { OnUpdate } from '@/_schemas/types';
import { nameResolver, resetFormState } from '@/_shared';

import { invalidateTeamMemberQuery } from '../apis';
import { fieldNames, teamMemberFormToUpdate, teamMemberQueryToForm } from '../converters';
import type { TeamMemberFormSchema, TeamMemberQuerySchema, TeamMemberUpdateSchema } from '../schemas';
const DATES = ['birthdate', 'hired_on', 'termination'];

export function useRevisionHistory(
  form: UseFormReturn<TeamMemberFormSchema>,
  onUpdate: OnUpdate<TeamMemberUpdateSchema, { data: TeamMemberQuerySchema }>
) {
  const validatePivot = useCallback((field: keyof TeamMemberFormSchema) => {
    return (
      field === 'phone_numbers' ||
      field === 'addresses' ||
      field === 'emergency_contact' ||
      field === 'profile_image' ||
      field === 'employment_agreement' ||
      field === 'handbook_acknowledgement' ||
      field === 'offer_letter' ||
      field === 'gallery' ||
      field === 'business_card_template' ||
      field === 'vector_signatured'
    );
  }, []);

  const pivotValueCalculator: PivotValueCalculator<TeamMemberFormSchema> = useCallback(
    (field, items) => {
      const allFormValues = form.getValues();
      if (validatePivot(field)) {
        const nV = teamMemberFormToUpdate({ ...allFormValues });
        let calcValue = nV[field];

        items.forEach(([value, type]) => {
          if (value !== null && typeof value !== 'undefined') {
            const oldValueIds = value[field]?.map((item) => item.id) ?? [];
            calcValue = newValueCalculator(oldValueIds, calcValue, type);
          }
        });
        return calcValue;
      }
      return [];
    },
    [form, validatePivot]
  );

  const onRevert: OnRevert<TeamMemberFormSchema> = useCallback(
    async (oldValue, field) => {
      const allFormValues = form.getValues();
      let nV: TeamMemberUpdateSchema = {} as TeamMemberUpdateSchema;
      const isPivot = !!field && validatePivot(field);

      if (!isPivot) {
        nV = teamMemberFormToUpdate({ ...allFormValues, ...oldValue });
      } else {
        nV = teamMemberFormToUpdate({ ...allFormValues });
        nV = { ...nV, [field]: oldValue };
      }

      const res = await onUpdate(nV, 'Team Member changes have been reverted.');
      if (res.data) {
        if (isPivot) {
          invalidateTeamMemberQuery(nV.id + '');
        } else {
          resetFormState(form, { ...allFormValues, ...oldValue });
        }
      }
    },
    [form, onUpdate, validatePivot]
  );

  const queryToFormConverter: QueryToFormConverter<TeamMemberQuerySchema, TeamMemberFormSchema> = useCallback((q) => {
    return teamMemberQueryToForm({ ...q });
  }, []);

  const nameConverter: NameConverter<TeamMemberFormSchema> = useCallback((f) => {
    // if key does not exist in fieldNames or field is not revertible
    if (!(typeof fieldNames[f] !== 'undefined' && fieldNames[f] !== null) || !fieldNames[f].revertible) return null;
    return fieldNames[f].name;
  }, []);

  const pivotClassifier: PivotClassifier<TeamMemberFormSchema> = useCallback(
    (f) => {
      const isPivot = validatePivot(f);
      let changeSetExtractorType: CHANGE_SET_EXTRACTOR_TYPE = null;

      if (isPivot) {
        switch (f) {
          case 'phone_numbers':
            changeSetExtractorType = 'PHONE_NUMBER_EMAIL';
            break;
          case 'addresses':
            changeSetExtractorType = 'ADDRESS';
            break;
          case 'emergency_contact':
            changeSetExtractorType = 'NAMED';
            break;
          case 'profile_image':
          case 'employment_agreement':
          case 'handbook_acknowledgement':
          case 'offer_letter':
          case 'gallery':
          case 'business_card_template':
          case 'vector_signatured':
            changeSetExtractorType = 'MEDIA';
            break;
        }
      }
      return { isPivot, changeSetExtractorType };
    },
    [validatePivot]
  );

  const valueConverter: ValueConverter<TeamMemberFormSchema> = useCallback((f, form) => {
    // if key does not exist in fieldNames or field is not revertible
    const v = form[f];
    if (
      typeof fieldNames[f] === 'undefined' ||
      fieldNames[f] === null ||
      !fieldNames[f].revertible ||
      v === null ||
      typeof v === 'undefined'
    ) {
      return null;
    }

    if (f === 'interests' && Array.isArray(v)) {
      return v.map((item) => ('name' in item ? item.name : '')).join(', ');
    } else if (typeof v === 'object') {
      if ('name' in v) {
        return v.name;
      } else if ('first_name' in v && 'last_name' in v) {
        return nameResolver(v.first_name, v.last_name);
      }

      return f;
    } else if (typeof v === 'string') {
      return historyItemStringConverter(v, DATES.includes(f));
    } else if (typeof v === 'number') {
      return v + '';
    }

    return null;
  }, []);

  return {
    queryToFormConverter,
    nameConverter,
    valueConverter,
    onRevert,
    validatePivot,
    pivotClassifier,
    pivotValueCalculator,
  };
}
